import type { ArtistDTO, EventDTO, SetDTO, StageDTO } from "../../server/queries";
import { loadSnapshot, snapshotSync } from "./snapshot";

/**
 * Resolve the API base URL depending on runtime.
 *
 *   - Browser/PWA: relative "/api" → same origin (Cloud Run in prod,
 *     localhost:3000 in dev via Vite proxy).
 *   - Capacitor (iOS/Android native): absolute URL to the Cloud Run
 *     instance; the WebView is served from a capacitor:// or file://
 *     scheme so relative paths can't reach our API.
 *   - Optional override: window.__LINEUP_API_BASE__ (e.g. for staging
 *     builds or testing).
 */
export function resolveApiBase(): string {
  if (typeof window === "undefined") return "/api";
  const w = window as unknown as {
    __LINEUP_API_BASE__?: string;
    Capacitor?: { isNativePlatform?: () => boolean };
  };
  if (typeof w.__LINEUP_API_BASE__ === "string" && w.__LINEUP_API_BASE__) {
    return w.__LINEUP_API_BASE__;
  }
  if (w.Capacitor?.isNativePlatform?.()) {
    return "https://checkthelineup.com/api";
  }
  return "/api";
}

const BASE = resolveApiBase();

// Kick the snapshot load as soon as the module is imported. Subsequent
// API calls can resolve from memory without an extra round trip.
if (typeof window !== "undefined") {
  void loadSnapshot();
}

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const r = await fetch(`${BASE}${path}`, {
    headers: { "Content-Type": "application/json" },
    ...init,
  });
  if (!r.ok) {
    const text = await r.text().catch(() => "");
    throw new Error(`${r.status} ${r.statusText} — ${text}`);
  }
  return r.json() as Promise<T>;
}

function qs(params: Record<string, string | number | undefined>): string {
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== "") usp.set(k, String(v));
  }
  const s = usp.toString();
  return s ? `?${s}` : "";
}

/**
 * Try the snapshot first; fall back to the network. `fn` extracts a value
 * from the snapshot or returns undefined if the data isn't present.
 */
async function fromSnapshotOrNetwork<T>(
  fn: (s: import("./snapshot").Snapshot) => T | undefined,
  networkFn: () => Promise<T>,
): Promise<T> {
  const sync = snapshotSync();
  if (sync) {
    const hit = fn(sync);
    if (hit !== undefined) return hit;
  } else {
    const s = await loadSnapshot();
    if (s) {
      const hit = fn(s);
      if (hit !== undefined) return hit;
    }
  }
  return networkFn();
}

function filterArtists(
  artists: ArtistDTO[],
  filters: { days?: number[]; stages?: string[]; genres?: string[]; q?: string },
): ArtistDTO[] {
  let out = artists;
  if (filters.days?.length) {
    const d = new Set(filters.days);
    out = out.filter(a => a.sets.some(s => d.has(s.day)));
  }
  if (filters.stages?.length) {
    const s = new Set(filters.stages);
    out = out.filter(a => a.sets.some(x => s.has(x.stage.slug)));
  }
  if (filters.genres?.length) {
    const g = new Set(filters.genres);
    out = out.filter(a => a.genre && g.has(a.genre));
  }
  if (filters.q) {
    const q = filters.q.toLowerCase();
    out = out.filter(a => a.name.toLowerCase().includes(q));
  }
  return out;
}

export const api = {
  events: () =>
    fromSnapshotOrNetwork(s => s.events, () => req<EventDTO[]>("/events")),
  event: (slug: string) =>
    fromSnapshotOrNetwork(
      s => s.events.find(e => e.slug === slug),
      () => req<EventDTO>(`/events/${encodeURIComponent(slug)}`),
    ),
  stages: (eventSlug: string) =>
    fromSnapshotOrNetwork(
      s => s.stages[eventSlug],
      () => req<StageDTO[]>(`/events/${encodeURIComponent(eventSlug)}/stages`),
    ),
  genres: (eventSlug: string) =>
    fromSnapshotOrNetwork(
      s => s.genres[eventSlug],
      () => req<string[]>(`/events/${encodeURIComponent(eventSlug)}/genres`),
    ),
  artists: (
    eventSlug: string,
    filters: {
      days?: number[];
      stages?: string[];
      genres?: string[];
      q?: string;
    } = {},
  ) =>
    fromSnapshotOrNetwork(
      s => {
        const list = s.artists[eventSlug];
        return list ? filterArtists(list, filters) : undefined;
      },
      () =>
        req<ArtistDTO[]>(
          `/events/${encodeURIComponent(eventSlug)}/artists${qs({
            days: filters.days?.length ? filters.days.join(",") : undefined,
            stages: filters.stages?.length ? filters.stages.join(",") : undefined,
            genres: filters.genres?.length ? filters.genres.join(",") : undefined,
            q: filters.q,
          })}`,
        ),
    ),
  artist: (eventSlug: string, artistSlug: string) =>
    fromSnapshotOrNetwork(
      s => s.artists[eventSlug]?.find(a => a.slug === artistSlug),
      () =>
        req<ArtistDTO>(
          `/events/${encodeURIComponent(eventSlug)}/artists/${encodeURIComponent(artistSlug)}`,
        ),
    ),
  coverage: () =>
    req<{
      total: number; photos: number; deezer_hits: number;
      research_done: number; research_partial: number; research_failed: number;
      yt_videos: number; verified_ig: number; verified_sc: number; verified_yt_channel: number;
      events: number;
    }>("/coverage"),
  createShare: (
    eventSlug: string,
    picks:
      | Record<string, "like" | "superlike">
      | {
          artists: Record<string, "like" | "superlike">;
          sets: Record<number, "like" | "superlike">;
        },
    name?: string | null,
    deviceId?: string | null,
  ) =>
    req<{ token: string; url: string }>("/shares", {
      method: "POST",
      body: JSON.stringify({
        eventSlug,
        picks,
        name: name ?? null,
        deviceId: deviceId ?? null,
      }),
    }),
  getShare: (token: string) =>
    req<{
      token: string;
      eventSlug: string;
      /** Nested for new shares; legacy shares come back as a flat artists
       *  map under `picks` (the server converts). Clients should expect
       *  either shape for now. */
      picks:
        | Record<string, "like" | "superlike">
        | {
            artists: Record<string, "like" | "superlike">;
            sets: Record<number, "like" | "superlike">;
          };
      name: string | null;
      createdAt: number;
      viewCount: number;
    }>(`/shares/${encodeURIComponent(token)}`),
};

export type { ArtistDTO, EventDTO, SetDTO, StageDTO };
